/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useDispatch } from 'react-redux';
import { css, StyleSheet } from 'aphrodite';
import React, { useState } from 'react';

import { colors } from '@blend/consumer-components';
import { AxiosError } from 'axios';
import { useRoom } from '../../hooks/useRoom';
import { Sidebar } from '../../components/DocumentManagement/Sidebar/Sidebar';
import { DocumentViewer } from '../../components/DocumentManagement/DocumentViewer/DocumentViewer';
import { DocumentToolbar } from '../../components/DocumentManagement/DocumentToolbar/DocumentToolbar';
import { DocumentsAPI } from '../../api/common/documents';
import { notaryAPI } from '../../api/notary';
import { NotificationItem } from '../../components/common/Notifications/NotificationItem';
import { addNotifications } from '../../actions/pageActions';
import SigningDocument from '../../components/common/Documents/SigningDocument';
import { NotaryHeader } from '../../components/NotaryApp/NotaryHeader/NotaryHeader';
import { Scrollable } from '../../components/Scrollable/Scrollable';
import { TaggingModal } from './TaggingModal';
import { FileId, Upload, UploadResponse, Uploads } from '@components/form/Inputs/DocUploader/types';
import { DocUploaderModal } from '@components/form/Inputs/DocUploader/DocUploaderModal';
import { diagnostics } from '@utils/diagnostics';
import { difference, omit } from '@utils/lodash';

export namespace DocumentManagementPage {
  export interface Props extends React.HTMLAttributes<HTMLDivElement> {}
}

const styles = StyleSheet.create({
  documentManagementPageWrapper: {
    height: '100%',
    width: '100%',
    backgroundColor: colors.clayMinus2,
    display: 'flex',
    justifyContent: 'center',
  },
  mainWrapper: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 1440,
    width: '100%',
    position: 'relative',
    backgroundColor: colors.snowPlus1,
  },
  bodyWrapper: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
  },
  contentWrapper: {
    flexGrow: 1,
    flexDirection: 'column',
    display: 'flex',
  },
});

// TODO (RON-523) - non-static error messages
const genericError: NotificationItem.PropsWithoutId = {
  type: 'error' as any,
  iconType: 'error',
  content: 'That action could not be completed. Try again.',
  shouldAutoRemove: true,
};

export const DocumentManagementPage: React.FC<DocumentManagementPage.Props> = () => {
  const dispatch = useDispatch();
  const { roomId } = useRoom();

  const [documents, setDocuments] = useState<DocumentsAPI.DocumentsResponse | null>(null);
  const [activePdfUrl, setActivePdfUrl] = useState<string | undefined>();
  const [activeEditUrl, setActiveEditUrl] = useState<string | undefined>();
  const [isUploaderOpen, setIsUploaderOpen] = useState<boolean>(false);
  const [isTaggingModalOpen, setIsTaggingModalOpen] = useState<boolean>(false);
  const [fileIds, setFileIds] = useState<FileId[]>([]);
  const [uploads, setUploads] = useState<Uploads>({});
  const [files, setFiles] = React.useState<File[]>([]);

  const fetchDocuments = async () => {
    const docs = await notaryAPI.documents.fetchRoomDocuments(roomId);
    const docsAreDifferent = JSON.stringify(docs) !== JSON.stringify(documents);
    if (docsAreDifferent) {
      setDocuments(docs);
    }
  };

  const onSelectDocument = async (document: SigningDocument) => {
    if (!document || !document.allowDownload) {
      setActivePdfUrl(undefined);
      setActiveEditUrl(undefined);
    } else {
      try {
        const fileUrl = await notaryAPI.documents.fetchRoomDocumentPdf(roomId, document.id);
        setActivePdfUrl(fileUrl);
        setActiveEditUrl(undefined);
      } catch (e) {
        addNotifications([genericError])(dispatch);
      }
    }
  };

  const fetchEditUrl = () => {
    setIsTaggingModalOpen(true);
    return;
  };

  const onFinalizeDocument = async (documentId: string) => {
    try {
      await notaryAPI.documents.finalizeRoomDocument(roomId, documentId);
      const fileUrl = await notaryAPI.documents.fetchRoomDocumentPdf(roomId, documentId);
      setActiveEditUrl(undefined);
      setActivePdfUrl(fileUrl);
    } catch (e) {
      addNotifications([
        {
          type: 'error' as any,
          iconType: 'error',
          content: (e as AxiosError<{ message?: string }>)?.response?.data?.message,
          shouldAutoRemove: true,
        },
      ])(dispatch);
    }
  };

  // uploadDocument is a required prop for consumer component file uploader. And it is required to be
  // a promise because borrower app uploads files to db on each upload. And put them in one pdf on submit.
  // But only the ones submitted will be valid doc for the borrower. However, all documents notary uploads
  // will be store unless they remove it from the sidebar. Therefore, here we store files in a state and
  // only upload on submit so that if the notary click 'Cancel', no file will be uploaded.
  const uploadDocument = (file: File): Promise<UploadResponse> => {
    setFiles([...files, file]);
    return new Promise(() => {
      return { data: { key: '', fullPath: '' } };
    });
  };

  const handleUpload = (fileId: FileId, upload: Upload) => {
    setFileIds([...fileIds, fileId]);
    setUploads({ ...uploads, [fileId]: upload });
  };

  const handleRemove = (fileId: FileId) => {
    setFileIds(difference(fileIds, [fileId]));
    setUploads(omit(uploads, [fileId]));
  };

  const removeAllUpload = () => {
    setFileIds([]);
    setFiles([]);
    setUploads({});
  };

  const closeUploader = () => {
    removeAllUpload();
    setIsUploaderOpen(false);
  };

  const handleSubmit = async () => {
    const maxSize = 32 * 1024 * 1024; // 32MB in bytes

    const oversizedFiles = files.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      throw new Error(
        `Total files size should not exceed the 32MB limit: ${oversizedFiles.map(f => f.name).join(', ')}`,
      );
    }

    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > maxSize) {
      throw new Error(
        `Total file size exceeds the 32MB limit. Current total: ${(totalSize / (1024 * 1024)).toFixed(2)}MB`,
      );
    }

    try {
      await Promise.all(files.map(file => notaryAPI.documents.uploadDocumentToRoom(roomId, file)));
    } catch {
      diagnostics.error(new Error(`Failed to upload file to roomId: ${roomId}`));
    }
    closeUploader();
  };

  return (
    <div className={css(styles.documentManagementPageWrapper)} data-it="DocumentManagementPage">
      <div className={css(styles.mainWrapper)}>
        <NotaryHeader />
        <div className={css(styles.bodyWrapper)}>
          <Sidebar
            ownerId={roomId}
            documents={documents}
            setDocuments={setDocuments}
            fetchDocuments={fetchDocuments}
            onSelectDocument={onSelectDocument}
            setIsUploaderOpen={setIsUploaderOpen}
          />
          <div className={css(styles.contentWrapper)}>
            <DocumentToolbar
              ownerId={roomId}
              fetchEditUrl={fetchEditUrl}
              onFinalizeDocument={onFinalizeDocument}
              isTaggingActive={!!activeEditUrl}
            />
            {/* TODO: RON-1531 DocumentViewer needs to know what eSignProvider is being used to render correctly */}
            <Scrollable>{<DocumentViewer activePdfUrl={activePdfUrl} activeEditUrl={activeEditUrl} />}</Scrollable>
          </div>
          <DocUploaderModal
            title={'Upload to Document Package'}
            label={'Upload to Document Package'}
            message={
              'You can upload multiple files. Supported file type is .pdf. Maximum total file size should be below 32MB.'
            }
            isOpen={isUploaderOpen}
            fileIds={fileIds}
            uploads={uploads}
            uploadFile={uploadDocument}
            onRemove={handleRemove}
            onUpload={handleUpload}
            onCancel={closeUploader}
            onSubmit={handleSubmit}
            disabled={false}
            customAllowedFileTypes={['pdf']}
          />
          {isTaggingModalOpen && <TaggingModal roomId={roomId} handleClose={() => setIsTaggingModalOpen(false)} />}
        </div>
      </div>
    </div>
  );
};
